import React, {useCallback, useEffect, useMemo} from 'react'
import styles from './UIGameTradeCreator.module.scss'
import clsx from 'clsx'
import {UIGameTradeCreatorProposal} from './UIGameTradeCreatorProposal'
import {UIGamePlayerCardInventory} from '../UIGamePlayerCardInventory'
import {UIGameTradeCreatorWantedCardSelector} from './UIGameTradeCreatorWantedCardSelector'
import {CardEnum} from '@colonist/game-states'
import {TradeCreationDataValidator, BankValidator, TradeCreationData, TradeUtils} from '@colonist/game-mechanic-validators'
import {ArrayUtils} from '@colonist/utils'
import {usePlayerValidators} from '../../store/selectors/usePlayerValidators'
import {useGameStore} from '../../store/GameStoreContext'
import {UIGameTradeCreatorActionButtons} from './UIGameTradeCreatorActionButtons'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'
import {useOfferedCardsSubsetOfInventory} from './useOfferedCardsSubsetOfInventory'

export interface UIGameTradeCreatorProps {
    className?: string | undefined
    isPortraitLayout?: boolean | undefined
}

export const UIGameTradeCreator = ({
    className,
    isPortraitLayout = false,
}: UIGameTradeCreatorProps) => {
    const gameState = useGameStore((state) => state.gameState)
    const gameSettings = useGameStore((state) => state.gameSettings)
    const myColor = useGameStore((state) => state.myColor)
    const {playerValidators, getPlayerValidator} = usePlayerValidators()
    const gameMode = useGameStore((state) => state.gameMode)
    const wantedCards = useGameStore((state) => state.gameTrade.wantedCards)
    const setWantedCards = useGameStore((state) => state.gameTrade.setWantedCards)
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const isBankTradeMode = useGameStore((state) => state.gameTrade.isBankTradeMode)
    const setIsShowTradePanel = useGameStore((state) => state.gameTrade.setIsShowTradePanel)
    const setIsBankTradeMode = useGameStore((state) => state.gameTrade.setIsBankTradeMode)
    const setTradeError = useGameStore((state) => state.gameTrade.setTradeError)
    const counterOffer = useGameStore((state) => state.gameTrade.counterOffer)
    const offeredCards = useGameStore((state) => state.gameCardInventory.selectedCards)
    const setOfferedCards = useGameStore((state) => state.gameCardInventory.setSelectedCards)
    const setOnSelectCardForTrade = useGameStore((state) => state.gameCardInventory.setOnSelectCardForTrade)
    const diceState = useGameStore((state) => state.gameState.diceState)
    const isActionBoxOpening = useGameStore((state) => state.actionBox.actionBoxData != null)
    const myPlayerValidator = getPlayerValidator(myColor)
    const currentStateValidator = useCurrentStateValidator()

    useOfferedCardsSubsetOfInventory()

    useEffect(() => {
        if(counterOffer) {
            const myCards = [...myPlayerValidator.resourceCards.cards]
            const givingResourceCards: CardEnum[] = []

            for(const card of counterOffer.givingResources) {
                const removed = ArrayUtils.removeElementFromArray(myCards, card)
                if(removed) {
                    givingResourceCards.push(card)
                }
            }

            setOfferedCards(givingResourceCards)
            setWantedCards(counterOffer.receivingResources)
            setIsShowTradePanel(true)
        }
    }, [counterOffer])

    useEffect(() => {
        if(!isShowTradePanel) {
            setOfferedCards([])
            setWantedCards([])
        }
    }, [isShowTradePanel])

    const removeCardTypesAndAnyCardFromOfferedCards = useCallback((cardTypes: CardEnum[]) => {
        const newOfferedCards = [...offeredCards.filter((prevCard) => !cardTypes.includes(prevCard) && prevCard != CardEnum.AnyResource)]
        setOfferedCards(newOfferedCards)
    }, [offeredCards])

    const removeAnyCardFromWantedCards = useCallback(() => {
        setWantedCards(wantedCards.filter((prevCard) => prevCard != CardEnum.AnyResource))
    }, [wantedCards])

    const addCardToOfferedCards = useCallback((card: CardEnum) => {
        if(offeredCards.includes(CardEnum.AnyResource) && card === CardEnum.AnyResource) {
            return
        }
        setOfferedCards([...offeredCards, card])
    }, [offeredCards])

    const addMultipleCardsToOfferedCards = useCallback((card: CardEnum, numberOfCardsToAdd: number) => {
        const newCards = Array.from({length: numberOfCardsToAdd}, () => card)
        setOfferedCards([...offeredCards, ...newCards])
    }, [offeredCards])

    const removeSingleCardFromOfferedCards = useCallback((card: CardEnum) => {
        const newOfferedCards = [...offeredCards]
        ArrayUtils.removeElementFromArray(newOfferedCards, card)
        setOfferedCards(newOfferedCards)
    }, [offeredCards])

    const removeMultipleCardsFromOfferedCards = useCallback((card: CardEnum, numberOfCardsToRemove: number) => {
        const newOfferedCards = [...offeredCards]
        for(let i = 0; i < numberOfCardsToRemove; i++) {
            ArrayUtils.removeElementFromArray(newOfferedCards, card)
        }
        setOfferedCards(newOfferedCards)
    }, [offeredCards])

    const onRemoveCardFromWantedCards = useCallback((card: CardEnum) => {
        const newWantedCards = [...wantedCards]
        ArrayUtils.removeElementFromArray(newWantedCards, card)
        setWantedCards(newWantedCards)
    }, [wantedCards])

    const onRemoveCardFromOfferedCards = useCallback((card: CardEnum) => {
        if(!isBankTradeMode) {
            removeSingleCardFromOfferedCards(card)
            return
        }
        const bankTradeRatios = myPlayerValidator.bankTradeRatiosState
        const tradeRatio = bankTradeRatios[card] ?? TradeUtils.DEFAULT_BANK_TRADE_RATIO
        const cardCount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card)

        let numberOfCardsToRemove = cardCount % tradeRatio
        numberOfCardsToRemove = numberOfCardsToRemove === 0 ? tradeRatio : numberOfCardsToRemove
        removeMultipleCardsFromOfferedCards(card, numberOfCardsToRemove)
    }, [
        isBankTradeMode,
        removeSingleCardFromOfferedCards,
        myPlayerValidator.bankTradeRatiosState,
        offeredCards,
        removeMultipleCardsFromOfferedCards,
    ])

    const onAddCardToOfferedCards = useCallback((card: CardEnum) => {
        if(!diceState.diceThrown) return
        const myResourceCards = [...myPlayerValidator.resourceCards.cards, CardEnum.AnyResource]
        const isNotResourceCard = !myResourceCards.includes(card)
        if(isNotResourceCard) return
        if(!isShowTradePanel) setIsShowTradePanel(true)
        if(!isBankTradeMode) {
            addCardToOfferedCards(card)
            return
        }
        const bankTradeRatios = myPlayerValidator.bankTradeRatiosState
        const requiredCardAmount = bankTradeRatios[card] ?? TradeUtils.DEFAULT_BANK_TRADE_RATIO
        const currentCardAmountInHand = myPlayerValidator.resourceCards.getCardCountForCard(card)
        const offeredCardAmount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card)
        const canAffordRatio = currentCardAmountInHand - offeredCardAmount >= requiredCardAmount
        if(!canAffordRatio) {
            setIsBankTradeMode(false)
            setTradeError({
                error: {
                    key: 'strings:game.errors.tradeErrors.invalidBankTradeRatios',
                },
                payload: {
                    hasBankTradeRatioError: true,
                },
            })
            addCardToOfferedCards(card)
            return
        }
        addMultipleCardsToOfferedCards(card, requiredCardAmount)
    }, [
        myPlayerValidator.resourceCards,
        isShowTradePanel,
        isBankTradeMode,
        addCardToOfferedCards,
        myPlayerValidator.bankTradeRatiosState,
        offeredCards,
        addMultipleCardsToOfferedCards,
        diceState.diceThrown,
    ])

    useEffect(() => {
        setOnSelectCardForTrade(onAddCardToOfferedCards)
    }, [onAddCardToOfferedCards])

    const onAddCardToWantedCards = useCallback((card: CardEnum) => {
        if(wantedCards.includes(CardEnum.AnyResource) && card === CardEnum.AnyResource) {
            return
        }
        const newWantedCards = [...wantedCards]
        newWantedCards.push(card)
        setWantedCards(newWantedCards)
    }, [wantedCards])

    const getCurrentTradeCreationData = useCallback((isBankTrade: boolean): TradeCreationData => ({
        creator: myColor,
        isBankTrade: isBankTrade,
        counterOfferInResponseToTradeId: counterOffer?.tradeId,
        offeredResources: offeredCards,
        wantedResources: wantedCards,
    }), [offeredCards, wantedCards, myColor, isBankTradeMode, counterOffer])

    const tradeCreationDataValidator = useMemo(() => {
        const activeResourceCardTypes = gameMode.activeResourceCardTypes
        const numberOfPlayers = playerValidators.length
        const myPlayerCards = myPlayerValidator.resourceCards.cards
        const isTradeCreatorBot = false
        const bankValidator = new BankValidator(gameState.bankState)
        const myBankTradeRatios = myPlayerValidator.bankTradeRatiosState
        const activeOffers = gameState.tradeState.activeOffers
        const closedOffers = gameState.tradeState.closedOffers
        const modeSetting = gameSettings.modeSetting
        const embargoState = gameState.tradeState.embargoState
        const currentTurnPlayerColors = currentStateValidator.currentTurnPlayers

        return new TradeCreationDataValidator(activeResourceCardTypes,
            numberOfPlayers,
            myPlayerCards,
            isTradeCreatorBot,
            bankValidator,
            myBankTradeRatios,
            activeOffers,
            closedOffers,
            modeSetting,
            embargoState,
            currentTurnPlayerColors)
    }, [gameState, gameSettings, gameMode, playerValidators, myColor, myPlayerValidator, currentStateValidator])

    const removeCardsIfInvalidBankRatio = useCallback(() => {
        const tradeCreationData = getCurrentTradeCreationData(true)
        const bankTradeError = tradeCreationDataValidator.getTradeCreationDataError(tradeCreationData)
        if(bankTradeError == null) return
        const bankTradeRatios = myPlayerValidator.bankTradeRatiosState
        const cardTypesToRemove: CardEnum[] = []
        for(const card of offeredCards) {
            const cardCount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card)
            const ratioForCard = bankTradeRatios[card] ?? TradeUtils.DEFAULT_BANK_TRADE_RATIO

            const isValidBankTradeRatio = cardCount % ratioForCard == 0
            if(isValidBankTradeRatio) continue
            cardTypesToRemove.push(card)
        }
        removeCardTypesAndAnyCardFromOfferedCards(cardTypesToRemove)
        removeAnyCardFromWantedCards()
    }, [
        getCurrentTradeCreationData,
        tradeCreationDataValidator,
        myPlayerValidator.bankTradeRatiosState,
        offeredCards,
        removeCardTypesAndAnyCardFromOfferedCards,
        removeAnyCardFromWantedCards,
    ])

    useEffect(() => {
        if(isBankTradeMode && isShowTradePanel) {
            removeCardsIfInvalidBankRatio()
        }
    }, [isBankTradeMode, isShowTradePanel])

    useEffect(() => {
        if(isActionBoxOpening) {
            setIsShowTradePanel(false)
        }
    }, [isActionBoxOpening])

    const inventoryBackground = useMemo(() => {
        if(!isPortraitLayout) return 'beige'
        return isShowTradePanel ? 'beige' : 'transparent'
    }, [isPortraitLayout, isShowTradePanel])

    return (
        <div className={clsx(styles.container, className, {
            [styles.portraitLayout]: isPortraitLayout,
        })}>
            <div className={styles.tradeCreatorContainer}>
                {isShowTradePanel && <>
                    <UIGameTradeCreatorWantedCardSelector onAddCard={onAddCardToWantedCards} isPortraitLayout={isPortraitLayout}/>
                    <UIGameTradeCreatorProposal
                        offeredCards={offeredCards}
                        wantedCards={wantedCards}
                        onClickOfferedCard={onRemoveCardFromOfferedCards}
                        onClickWantedCard={onRemoveCardFromWantedCards}
                        isPortraitLayout={isPortraitLayout}
                    />
                </>}
                <UIGamePlayerCardInventory
                    background={inventoryBackground}
                    disableTooltip={isPortraitLayout}
                    disableCardScaleOnHover={isPortraitLayout} />
            </div>
            {isShowTradePanel && <UIGameTradeCreatorActionButtons
                isPortraitLayout={isPortraitLayout}
                tradeCreationDataValidator={tradeCreationDataValidator}
                getCurrentTradeCreationData={getCurrentTradeCreationData}/>}
        </div>
    )
}
